import {
  OplAddressCity,
  OplAddressStreet,
  OplAddressZipCode
} from "@closerplatform/spinner-openapi";
import React, { useEffect, useState } from "react";
import { WidgetCheckboxInput } from "@fidget/common/components/inputs/widgetCheckboxInput/widgetCheckboxInput";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { LimitedWidgetInputProps } from "../messageWidgetForm/types";
import { OplAddressFormInputs } from "../messageWidgetForm/utils/oplAdressFormUtils";
import styles from "./addressFormChatItem.css";
import { getCities, getStreets, getZipCodes } from "./api";
import { TextInput } from "./components/addressFormInput/textInput";
import { ComboBox } from "./components/comboBox/comboBox";

interface Props extends LimitedWidgetInputProps {
  onCanSubmitChange: (canSubmit: boolean) => void;
}

interface WithQuery<T> {
  item?: T;
  query: string;
}

export const AddressFormChatItem = ({
  onChange,
  userAnswers,
  onCanSubmitChange,
  isSubmitted,
  formId
}: Props) => {
  const [city, setCity] = useState<WithQuery<OplAddressCity>>();
  const [street, setStreet] = useState<WithQuery<OplAddressStreet>>();
  const [zipCode, setZipCode] = useState<WithQuery<OplAddressZipCode>>();

  const noStreet = !!userAnswers[OplAddressFormInputs.NoStreet]?.value;

  useEffect(() => {
    onCanSubmitChange(
      !!userAnswers[OplAddressFormInputs.City]!.value &&
        (!!userAnswers[OplAddressFormInputs.NoStreet]!.value ||
          !!userAnswers[OplAddressFormInputs.Street]!.value) &&
        !!userAnswers[OplAddressFormInputs.StreetNumber]!.value &&
        !!userAnswers[OplAddressFormInputs.ZipCode]!.value
    );
  }, [userAnswers]);

  return (
    <fieldset disabled={isSubmitted} className={styles.container}>
      <div className={styles.full}>
        {isSubmitted ? (
          <TextInput value={userAnswers[OplAddressFormInputs.City]?.value ?? ""} />
        ) : (
          <ComboBox<OplAddressCity>
            label={intl.formatMessage("widget.addressForm.city.label")}
            itemsProvider={(query) => getCities(query)}
            keyExtractor={(item) => item.id.toString()}
            itemToString={(item) => item.name}
            onItemPick={(item) => {
              setCity({ item, query: item.name });
              onChange({ inputName: OplAddressFormInputs.City, value: item.name });
            }}
            onQueryChange={(query) => {
              setCity({ ...city, query });
              onChange({ inputName: OplAddressFormInputs.City, value: query });
            }}
          />
        )}
      </div>
      <div className={styles.full}>
        <WidgetCheckboxInput
          id={formId + OplAddressFormInputs.NoStreet}
          onChange={(checked) =>
            onChange({
              inputName: OplAddressFormInputs.NoStreet,
              value: checked ? OplAddressFormInputs.NoStreet : ""
            })
          }
          checked={noStreet}
          label={intl.formatMessage("widget.addressForm.noStreet.label")}
          className={styles.checkbox}
        />
      </div>
      <div className={styles.full}>
        {isSubmitted ? (
          <TextInput value={userAnswers[OplAddressFormInputs.Street]?.value ?? ""} />
        ) : (
          <ComboBox<OplAddressStreet>
            disabled={noStreet}
            label={intl.formatMessage("widget.addressForm.street.label")}
            itemsProvider={(query) => (city?.item?.id ? getStreets(query, city.item.id) : [])}
            keyExtractor={(item) => item.id.toString()}
            itemToString={(item) => item.name}
            onItemPick={(item) => {
              setStreet({ item, query: item.name });
              onChange({ inputName: OplAddressFormInputs.Street, value: item.name });
            }}
            onQueryChange={(query) => {
              setStreet({ ...street, query });
              onChange({ inputName: OplAddressFormInputs.Street, value: query });
            }}
          />
        )}
      </div>
      <div className={styles.half}>
        <TextInput
          onChange={(e) =>
            onChange({
              inputName: OplAddressFormInputs.StreetNumber,
              value: (e.target as HTMLInputElement).value
            })
          }
          value={userAnswers[OplAddressFormInputs.StreetNumber]?.value ?? ""}
          label={intl.formatMessage("widget.addressForm.streetNumber.label")}
        />
      </div>
      <div className={styles.half}>
        <TextInput
          onChange={(e) =>
            onChange({
              inputName: OplAddressFormInputs.ApartmentNumber,
              value: (e.target as HTMLInputElement).value
            })
          }
          value={userAnswers[OplAddressFormInputs.ApartmentNumber]?.value ?? ""}
          label={intl.formatMessage("widget.addressForm.apartmentNumber.label")}
        />
      </div>
      <div className={styles.full}>
        {isSubmitted ? (
          <TextInput value={userAnswers[OplAddressFormInputs.ZipCode]?.value ?? ""} />
        ) : (
          <ComboBox<OplAddressZipCode>
            label={intl.formatMessage("widget.addressForm.zipCode.label")}
            itemsProvider={() =>
              city?.item?.id && street?.item?.id
                ? getZipCodes(
                    userAnswers[OplAddressFormInputs.StreetNumber]!.value!,
                    street.item.id,
                    city.item.id
                  )
                : []
            }
            keyExtractor={(item) => item.value}
            itemToString={(item) => item.value}
            onItemPick={(item) => {
              setZipCode({ item, query: item.value });
              onChange({ inputName: OplAddressFormInputs.ZipCode, value: item.value });
            }}
            onQueryChange={(query) => {
              setZipCode({ ...zipCode, query });
              onChange({ inputName: OplAddressFormInputs.ZipCode, value: query });
            }}
            minProviderQueryLength={0}
          />
        )}
      </div>
    </fieldset>
  );
};
